import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { vocabAPI } from '../../api/client'
import type { UserVocabulary, VocabRecallResult } from '@enghustle/shared'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

/** FR-007 Spaced Repetition Vocabulary System + LME-1 Active Recall / LME-2 Contextual Examples */
export default function VocabPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: dueWords = [], isLoading } = useQuery({ queryKey: ['vocab-due'], queryFn: vocabAPI.due })
  const { data: stats } = useQuery({ queryKey: ['vocab-dashboard'], queryFn: vocabAPI.dashboard })
  // LME-1/2: which learning methods the admin has enabled for this user
  const { data: methods } = useQuery({ queryKey: ['vocab-methods'], queryFn: vocabAPI.methods })
  const activeRecall = methods?.activeRecall ?? false
  const contextExamples = methods?.contextExamples ?? false
  const fsrs = methods?.fsrs ?? false

  const [currentIdx, setCurrentIdx] = useState(0)
  const [showAnswer, setShowAnswer] = useState(false)
  const [typed, setTyped] = useState('')
  const [recall, setRecall] = useState<VocabRecallResult | null>(null)

  const current = (dueWords as UserVocabulary[])[currentIdx]
  const done = dueWords.length > 0 && currentIdx >= dueWords.length
  const pct = dueWords.length > 0 ? Math.round((currentIdx / dueWords.length) * 100) : 0

  // The card is "revealed" after the answer button (classic) or after a recall
  // attempt (active recall). Examples are fetched once revealed, gated on the flag.
  const revealed = activeRecall ? !!recall : showAnswer

  // LME-2: fetch example sentences once the card is revealed (both modes).
  const { data: examplesData } = useQuery({
    queryKey: ['vocab-examples', current?.id],
    queryFn: () => vocabAPI.examples(current!.id),
    enabled: !!current && revealed && contextExamples,
  })

  function resetCard() {
    setShowAnswer(false)
    setTyped('')
    setRecall(null)
  }

  function goNext() {
    resetCard()
    setCurrentIdx((i) => i + 1)
    qc.invalidateQueries({ queryKey: ['vocab-due'] })
    qc.invalidateQueries({ queryKey: ['vocab-dashboard'] })
  }

  const reviewMut = useMutation({
    mutationFn: ({ wordId, quality }: { wordId: string; quality: number }) => vocabAPI.review(wordId, quality),
    onSuccess: goNext,
  })

  const recallMut = useMutation({
    mutationFn: ({ wordId, answer }: { wordId: string; answer: string }) => vocabAPI.reviewRecall(wordId, answer),
    onSuccess: (res: any) => setRecall(res as VocabRecallResult),
  })

  // Examples/definition to show under the revealed answer. Prefer freshly fetched
  // (flag-gated) examples, then any already stored on the card / recall response.
  const examples = examplesData?.examples ?? current?.examples ?? (activeRecall ? recall?.examples : []) ?? []
  const definition = examplesData?.definition ?? current?.definition ?? (activeRecall ? recall?.definition : null) ?? null

  return (
    <AppLayout>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#8e00d5', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>style</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8e00d5' }}>Từ vựng SRS</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          Ôn từ vựng <span style={{ color: '#8e00d5', fontStyle: 'italic' }}>thông minh</span>
        </H>
        <p style={{ color: '#535b71', fontSize: '1rem', lineHeight: 1.6 }}>
          Hệ thống SM-2 — AI sắp xếp từ cần ôn theo đúng lúc bạn sắp quên.
        </p>
        {/* Method badges — signal which research-backed methods are active */}
        {(activeRecall || contextExamples || fsrs) && (
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
            {activeRecall && <MethodBadge icon="keyboard" label="Active Recall — tự gõ đáp án" />}
            {contextExamples && <MethodBadge icon="format_quote" label="Học qua câu ví dụ" />}
            {fsrs && <MethodBadge icon="neurology" label="Lịch ôn FSRS" />}
          </div>
        )}
      </div>

      {/* Stats row */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(8rem, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {[
            { label: 'Tổng từ', value: stats.total, icon: 'library_books', color: '#004be2', bg: '#eef0ff' },
            { label: 'Đã thành thạo', value: stats.mastered, icon: 'stars', color: '#8e00d5', bg: '#f5e6ff' },
            { label: 'Cần ôn hôm nay', value: stats.dueToday, icon: 'schedule', color: '#b41340', bg: '#ffe8ed' },
          ].map(item => (
            <div key={item.label} style={{ background: 'white', borderRadius: '1.25rem', padding: '1.25rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ width: '2.75rem', height: '2.75rem', borderRadius: '0.875rem', background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ color: item.color, fontSize: '1.375rem', fontVariationSettings: "'FILL' 1" }}>{item.icon}</span>
              </div>
              <div>
                <H style={{ display: 'block', fontSize: '1.5rem', fontWeight: 900, color: '#272e42' }}>{item.value}</H>
                <span style={{ fontSize: '0.75rem', color: '#535b71', fontWeight: 600 }}>{item.label}</span>
              </div>
            </div>
          ))}
          {/* G-6: Anki Export button */}
          {stats.total > 0 && (
            <div style={{ background: 'white', borderRadius: '1.25rem', padding: '1.25rem', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ width: '2.75rem', height: '2.75rem', borderRadius: '0.875rem', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ color: '#b45309', fontSize: '1.375rem', fontVariationSettings: "'FILL' 1" }}>download</span>
              </div>
              <div style={{ flex: 1 }}>
                <H style={{ display: 'block', fontSize: '0.875rem', fontWeight: 800, color: '#272e42', marginBottom: '0.25rem' }}>Xuất Anki</H>
                <button
                  onClick={() => vocabAPI.exportAnki().catch(() => {})}
                  style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                >
                  Tải file .txt →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <div className="spinner" />
        </div>
      ) : done ? (
        /* Completion */
        <div style={{ background: 'linear-gradient(135deg, #8e00d5, #eac4ff)', borderRadius: '2rem', padding: '3rem', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', bottom: '-2rem', right: '-2rem', width: '12rem', height: '12rem', background: 'rgba(0,75,226,0.3)', borderRadius: '50%', filter: 'blur(40px)' }} />
          <div style={{ fontSize: '4rem', marginBottom: '1rem', position: 'relative' }}>🎉</div>
          <H style={{ display: 'block', fontSize: '1.75rem', fontWeight: 900, color: 'white', marginBottom: '0.5rem', position: 'relative' }}>Ôn xong rồi!</H>
          <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '2rem', position: 'relative' }}>
            Đã ôn {Math.min(currentIdx, dueWords.length)} từ hôm nay. Tuyệt vời!
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', position: 'relative', flexWrap: 'wrap' }}>
            <button onClick={() => { setCurrentIdx(0); resetCard() }} style={{ padding: '0.875rem 1.75rem', borderRadius: '9999px', border: '2px solid rgba(255,255,255,0.4)', background: 'transparent', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer' }}>
              Ôn lại từ đầu
            </button>
            <button onClick={() => navigate('/vocab/decks')} style={{ padding: '0.875rem 1.75rem', borderRadius: '9999px', border: 'none', background: 'white', color: '#8e00d5', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer' }}>
              Quản lý bộ thẻ →
            </button>
          </div>
        </div>
      ) : dueWords.length === 0 ? (
        /* No due words */
        <div style={{ background: 'white', borderRadius: '2rem', padding: '3rem', textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.04)' }}>
          <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: '#a5adc6', display: 'block', marginBottom: '1rem' }}>check_circle</span>
          <H style={{ display: 'block', fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.5rem' }}>Không có từ nào cần ôn hôm nay!</H>
          <p style={{ color: '#535b71', marginBottom: '0.75rem' }}>Bạn đã ôn hết rồi. Quay lại vào ngày mai.</p>
          <p style={{ color: '#a5adc6', fontSize: '0.8125rem', marginBottom: '2rem' }}>Từ mới lưu hôm nay sẽ xuất hiện để ôn vào ngày mai theo hệ thống SM-2.</p>
          <button onClick={() => navigate('/vocab/decks')} style={{ padding: '0.875rem 2rem', borderRadius: '9999px', border: 'none', background: 'linear-gradient(135deg, #8e00d5, #eac4ff)', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer' }}>
            Khám phá bộ thẻ từ vựng →
          </button>
        </div>
      ) : (
        /* Flashcard */
        <div style={{ maxWidth: '40rem', margin: '0 auto' }}>
          {/* Progress */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#535b71' }}>Tiến độ hôm nay</span>
            <H style={{ fontWeight: 800, color: '#8e00d5' }}>{currentIdx + 1} / {dueWords.length}</H>
          </div>
          <div style={{ width: '100%', height: '0.375rem', background: '#d1dcff', borderRadius: '9999px', marginBottom: '2rem', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #8e00d5, #eac4ff)', borderRadius: '9999px', transition: 'width 0.5s' }} />
          </div>

          {/* Card */}
          <div style={{ background: 'white', borderRadius: '2rem', padding: '2.5rem', boxShadow: '0 20px 40px rgba(142,0,213,0.08)', textAlign: 'center', minHeight: '16rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {/* Context sentence with the target word blanked out */}
            <p style={{ fontSize: '1.125rem', lineHeight: 1.8, color: '#272e42', marginBottom: '2rem', maxWidth: '28rem' }}>
              {current.contextSentence?.replace(current.word, '________') ?? '________'}
            </p>

            {activeRecall ? (
              /* ── LME-1 ACTIVE RECALL MODE ─────────────────────────── */
              !recall ? (
                <form
                  onSubmit={(e) => { e.preventDefault(); if (typed.trim()) recallMut.mutate({ wordId: current.id, answer: typed }) }}
                  style={{ width: '100%', maxWidth: '24rem' }}
                >
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, color: '#8e00d5', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Gõ từ còn thiếu
                  </label>
                  <input
                    autoFocus
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder="Nhập từ bạn nhớ…"
                    style={{
                      width: '100%', padding: '0.875rem 1.25rem', borderRadius: '9999px', border: '2px solid #eac4ff',
                      fontSize: '1.125rem', textAlign: 'center', outline: 'none', fontFamily: "'Space Grotesk', sans-serif",
                      fontWeight: 700, color: '#272e42', marginBottom: '1.25rem',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = '#8e00d5' }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = '#eac4ff' }}
                  />
                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="submit"
                      disabled={!typed.trim() || recallMut.isPending}
                      style={{
                        padding: '0.875rem 2rem', borderRadius: '9999px', border: 'none',
                        background: typed.trim() ? 'linear-gradient(135deg, #8e00d5, #eac4ff)' : '#e5e0ec',
                        color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
                        cursor: typed.trim() ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {recallMut.isPending ? 'Đang kiểm tra…' : 'Kiểm tra'}
                    </button>
                    <button
                      type="button"
                      onClick={() => recallMut.mutate({ wordId: current.id, answer: '' })}
                      disabled={recallMut.isPending}
                      style={{
                        padding: '0.875rem 1.5rem', borderRadius: '9999px', border: '2px solid #e5e0ec',
                        background: 'transparent', color: '#535b71', fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 700, cursor: 'pointer',
                      }}
                    >
                      Không nhớ
                    </button>
                  </div>
                </form>
              ) : (
                /* Recall feedback + reveal */
                <div style={{ width: '100%' }}>
                  <RecallFeedback result={recall} />
                  <H style={{ display: 'block', fontSize: '2.5rem', fontWeight: 900, color: '#8e00d5', margin: '0.75rem 0 0.5rem' }}>
                    {recall.word}
                  </H>
                  {definition && (
                    <p style={{ color: '#535b71', fontSize: '1rem', marginBottom: '1rem', fontStyle: 'italic' }}>{definition}</p>
                  )}
                  {contextExamples && <ExampleList examples={examples} />}
                  <button
                    onClick={goNext}
                    style={{
                      marginTop: '1.5rem', padding: '0.875rem 2.5rem', borderRadius: '9999px', border: 'none',
                      background: 'linear-gradient(135deg, #8e00d5, #eac4ff)', color: 'white',
                      fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer',
                    }}
                  >
                    Từ tiếp theo →
                  </button>
                </div>
              )
            ) : !showAnswer ? (
              /* ── CLASSIC MODE: reveal then self-grade ─────────────── */
              <button
                onClick={() => setShowAnswer(true)}
                style={{
                  padding: '1rem 2.5rem', borderRadius: '9999px', border: '2px solid rgba(142,0,213,0.3)',
                  background: 'transparent', color: '#8e00d5', fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(142,0,213,0.06)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
              >
                Xem đáp án
              </button>
            ) : (
              <div style={{ width: '100%' }}>
                <H style={{ display: 'block', fontSize: '2.5rem', fontWeight: 900, color: '#8e00d5', marginBottom: '0.5rem' }}>
                  {current.word}
                </H>
                {definition && (
                  <p style={{ color: '#535b71', fontSize: '1rem', marginBottom: '0.75rem', fontStyle: 'italic' }}>{definition}</p>
                )}
                {contextExamples && <ExampleList examples={examples} />}
                <p style={{ color: '#a5adc6', fontSize: '0.875rem', margin: '1.25rem 0 1rem', fontWeight: 600 }}>Bạn nhớ từ này như thế nào?</p>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {[
                    { quality: 1, label: 'Không nhớ', color: '#b41340', bg: 'rgba(180,19,64,0.08)' },
                    { quality: 3, label: 'Khó nhớ',   color: '#b45309', bg: 'rgba(180,83,9,0.08)' },
                    { quality: 5, label: 'Nhớ rõ',    color: '#8e00d5', bg: 'rgba(142,0,213,0.08)' },
                  ].map(opt => (
                    <button
                      key={opt.quality}
                      onClick={() => reviewMut.mutate({ wordId: current.id, quality: opt.quality })}
                      disabled={reviewMut.isPending}
                      style={{
                        padding: '0.875rem 1.75rem', borderRadius: '9999px', border: 'none',
                        background: opt.bg, color: opt.color,
                        fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.9375rem',
                        cursor: 'pointer', transition: 'all 0.2s',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Dot row */}
          <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'center', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            {(dueWords as UserVocabulary[]).map((_, i) => (
              <div key={i} style={{
                width: '0.5rem', height: '0.5rem', borderRadius: '50%',
                background: i < currentIdx ? '#8e00d5' : i === currentIdx ? '#8e00d5' : '#d1dcff',
                opacity: i < currentIdx ? 0.4 : 1,
                transition: 'all 0.3s',
              }} />
            ))}
          </div>
        </div>
      )}
    </AppLayout>
  )
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function MethodBadge({ icon, label }: { icon: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.75rem', borderRadius: '9999px', background: '#f5e6ff', color: '#8e00d5', fontSize: '0.75rem', fontWeight: 700 }}>
      <span className="material-symbols-outlined" style={{ fontSize: '0.9375rem', fontVariationSettings: "'FILL' 1" }}>{icon}</span>
      {label}
    </span>
  )
}

/** LME-1: colored banner reflecting how the typed answer matched. */
function RecallFeedback({ result }: { result: VocabRecallResult }) {
  const map = {
    exact: { bg: '#e7f9ef', color: '#0f9d58', icon: 'check_circle', text: 'Chính xác! 🎉' },
    close: { bg: '#fff4e5', color: '#b45309', icon: 'error', text: 'Gần đúng — chú ý chính tả' },
    wrong: { bg: '#ffe8ed', color: '#b41340', icon: 'cancel', text: 'Chưa đúng — ôn lại nhé' },
  }[result.match]
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '9999px', background: map.bg, color: map.color, fontWeight: 700, fontSize: '0.9375rem' }}>
      <span className="material-symbols-outlined" style={{ fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>{map.icon}</span>
      {map.text}
    </div>
  )
}

/** LME-2: render 2–3 example sentences in a soft card. */
function ExampleList({ examples }: { examples: string[] }) {
  if (!examples || examples.length === 0) return null
  return (
    <div style={{ textAlign: 'left', background: '#faf5ff', borderRadius: '1rem', padding: '1rem 1.25rem', marginTop: '0.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.5rem' }}>
        <span className="material-symbols-outlined" style={{ color: '#8e00d5', fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>format_quote</span>
        <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#8e00d5' }}>Ví dụ trong ngữ cảnh</span>
      </div>
      <ul style={{ margin: 0, paddingLeft: '1.1rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {examples.map((ex, i) => (
          <li key={i} style={{ color: '#3f4560', fontSize: '0.9375rem', lineHeight: 1.6 }}>{ex}</li>
        ))}
      </ul>
    </div>
  )
}
